import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import HierarchicalCheckbox from './HierarchicalCheckbox';
import { fetchHHAreasTree, type AreaOption } from '../../services/hhDictionariesService';
import { ReactComponent as FlagRussia } from '../../assets/flags/flag-russia.svg';
import { ReactComponent as FlagKazakhstan } from '../../assets/flags/flag-kazakhstan.svg';
import { ReactComponent as FlagAzerbaijan } from '../../assets/flags/flag-azerbaijan.svg';
import { ReactComponent as FlagBelarus } from '../../assets/flags/flag-belarus.svg';
import { ReactComponent as FlagGeorgia } from '../../assets/flags/flag-georgia.svg';
import { ReactComponent as FlagKyrgyzstan } from '../../assets/flags/flag-kyrgyzstan.svg';
import { ReactComponent as FlagUzbekistan } from '../../assets/flags/flag-uzbekistan.svg';

interface HierarchicalItem {
  id: string;
  label: string;
  count?: number;
  children?: HierarchicalItem[];
}

interface AreaModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAreas: string[];
  onApply: (areas: string[]) => void;
  singleSelect?: boolean;
  title?: string;
  subtitle?: string;
  /**
   * Per-source загрузчик списка регионов. Если undefined — fallback на
   * fetchHHAreasTree (обратная совместимость со старыми вызовами).
   * Передаётся из sourceConfig.dataLoaders.locations.
   */
  fetchData?: () => Promise<AreaOption[]>;
  /**
   * Скрыть переключатель стран (флаги Россия/Казахстан/...). Нужно для
   * источников где `countryId` у регионов не проставлен (например Avito —
   * Locations.xml содержит только Россию).
   */
  hideCountryTabs?: boolean;
}

type CountryCode = 'RU' | 'KZ' | 'AZ' | 'BY' | 'GE' | 'KG' | 'UZ';

const COUNTRY_CONFIG: Record<CountryCode, { id: string; name: string }> = {
  RU: { id: '113', name: 'Россия' },
  KZ: { id: '40', name: 'Казахстан' },
  AZ: { id: '9', name: 'Азербайджан' },
  BY: { id: '16', name: 'Беларусь' },
  GE: { id: '28', name: 'Грузия' },
  KG: { id: '48', name: 'Кыргызстан' },
  UZ: { id: '97', name: 'Узбекистан' },
};

const AreaModal: React.FC<AreaModalProps> = ({
  isOpen,
  onClose,
  selectedAreas,
  onApply,
  singleSelect = false,
  title = 'География',
  subtitle,
  fetchData,
  hideCountryTabs = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [areas, setAreas] = useState<AreaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>('RU');
  const [draftSelectedAreas, setDraftSelectedAreas] = useState<string[]>([]);
  const [didExpandInitialSelection, setDidExpandInitialSelection] = useState(false);

  useEffect(() => {
    const loadAreas = async () => {
      if (!isOpen) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Per-source загрузчик из sourceConfig.dataLoaders.locations.
        // Если не передан — fallback на hh (обратная совместимость).
        const loader = fetchData ?? fetchHHAreasTree;
        const data = await loader();
        setAreas(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Не удалось получить список регионов');
        setAreas([]);
      } finally {
        setLoading(false);
      }
    };

    void loadAreas();
    // fetchData в deps: при переключении source между hh/avito функция
    // меняется → надо перезагрузить данные. Иначе при первом открытии
    // модалки запомнятся hh-данные и не сменятся.
  }, [isOpen, fetchData]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setExpandedItems([]);
    setDraftSelectedAreas((selectedAreas || []).map(String));
    setDidExpandInitialSelection(false);
  }, [isOpen, selectedAreas]);

  const { idToArea, idToDescendants } = useMemo(() => {
    const idToArea = new Map<string, AreaOption>();
    const idToDescendants = new Map<string, string[]>();

    const build = (items: AreaOption[]) => {
      for (const item of items) {
        const id = String(item.id);
        idToArea.set(id, item);
        if (item.areas && item.areas.length > 0) {
          build(item.areas);
        }
      }
    };
    build(areas || []);

    const collectDescendants = (node: AreaOption): string[] => {
      const cached = idToDescendants.get(String(node.id));
      if (cached) return cached;
      const acc: string[] = [];
      if (node.areas && node.areas.length > 0) {
        for (const child of node.areas) {
          acc.push(String(child.id));
          const childDesc = collectDescendants(child);
          if (childDesc.length > 0) acc.push(...childDesc);
        }
      }
      idToDescendants.set(String(node.id), acc);
      return acc;
    };

    Array.from(idToArea.values()).forEach((area) => {
      collectDescendants(area);
    });

    return { idToArea, idToDescendants };
  }, [areas]);

  useEffect(() => {
    if (!isOpen) return;
    if (didExpandInitialSelection) return;
    if (!areas || areas.length === 0) return;
    if (singleSelect) { setDidExpandInitialSelection(true); return; }

    const expanded = new Set<string>();
    const selectedSet = new Set((selectedAreas || []).map(String));
    const selectedCountryIds = (Object.keys(COUNTRY_CONFIG) as CountryCode[])
      .map((code) => COUNTRY_CONFIG[code].id)
      .filter((id) => selectedSet.has(String(id)));

    if (selectedCountryIds.length > 0) {
      const hasCountryIdField = (areas || []).some((a) => (a as any).countryId);
      selectedCountryIds.forEach((countryId) => {
        let countryItems: AreaOption[] = [];
        if (hasCountryIdField) {
          countryItems = (areas || []).filter((a) => String((a as any).countryId) === String(countryId));
        } else {
          const root = (areas || []).find((a) => String(a.id) === String(countryId));
          countryItems = (root as any)?.areas || [];
        }
        if (!countryItems || countryItems.length === 0) return;
        const hierarchy = convertToHierarchicalItems(countryItems);
        hierarchy.forEach((region) => {
          expanded.add(String(region.id));
          (region.children || []).forEach((child) => expanded.add(String(child.id)));
        });
      });
    }
    for (const id of (selectedAreas || []).map(String)) {
      expanded.add(id);
      const node = idToArea.get(id);
      if (node) {
        const desc = idToDescendants.get(String(node.id)) || [];
        desc.forEach((d) => expanded.add(d));
      }
    }
    setDraftSelectedAreas(Array.from(expanded));
    setDidExpandInitialSelection(true);
  }, [isOpen, didExpandInitialSelection, areas, selectedAreas, idToArea, idToDescendants]);

  const convertToHierarchicalItems = (items: AreaOption[]): HierarchicalItem[] => {
    const collectLeafAreas = (item: AreaOption, acc: AreaOption[]) => {
      if (!item.areas || item.areas.length === 0) {
        acc.push(item);
        return;
      }
      item.areas.forEach(child => collectLeafAreas(child, acc));
    };

    return items
      .map(region => {
        const leafAreas: AreaOption[] = [];
        collectLeafAreas(region, leafAreas);

        const children: HierarchicalItem[] = leafAreas.map(city => ({
          id: city.id,
          label: city.name
        }));

        return {
          id: region.id,
          label: region.name,
          children
        };
      })
      .filter(regionItem => regionItem.children && regionItem.children.length > 0);
  };

  const filteredAreas = useMemo(() => {
    if (!areas || areas.length === 0) {
      return [];
    }

    const hasCountryId = areas.some((area) => area.countryId);
    if (!hasCountryId) {
      return areas;
    }

    const targetCountryId = COUNTRY_CONFIG[selectedCountry].id;
    return areas.filter((area) => area.countryId === targetCountryId);
  }, [areas, selectedCountry]);

  const areaDataByCountry = useMemo(
    () => convertToHierarchicalItems(filteredAreas),
    [filteredAreas]
  );

  const getAllChildrenIds = (item: HierarchicalItem): string[] => {
    const childrenIds: string[] = [];
    if (item.children) {
      item.children.forEach(child => {
        childrenIds.push(child.id);
        childrenIds.push(...getAllChildrenIds(child));
      });
    }
    return childrenIds;
  };

  const findItemById = (items: HierarchicalItem[], id: string): HierarchicalItem | null => {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const handleToggle = (id: string, checked: boolean) => {
    const sourceData = areaDataByCountry;
    const item = findItemById(sourceData, id);
    if (!item) {
      return;
    }

    // Режим выбора одного города: выбираем только листовые элементы (города)
    if (singleSelect) {
      if (!item.children || item.children.length === 0) {
        if (!checked) {
          setDraftSelectedAreas([]);
        } else {
          setDraftSelectedAreas([id]);
        }
      }
      return;
    }

    const childrenIds = getAllChildrenIds(item);
    const affectedIds = [id, ...childrenIds];

    if (checked) {
      setDraftSelectedAreas((prev) => {
        const set = new Set(prev.map(String));
        affectedIds.forEach((affectedId) => set.add(String(affectedId)));
        return Array.from(set);
      });
    } else {
      const countryId = COUNTRY_CONFIG[selectedCountry].id;
      setDraftSelectedAreas((prev) =>
        prev.filter((selectedId) => {
          const sid = String(selectedId);
          return !affectedIds.includes(sid) && sid !== String(countryId);
        })
      );
    }
  };

  const handleToggleExpanded = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleReset = () => {
    setDraftSelectedAreas([]);
  };

  const handleSave = () => {
    onApply(draftSelectedAreas);
    onClose();
  };

  const filterData = (items: HierarchicalItem[], searchTerm: string): HierarchicalItem[] => {
    if (!searchTerm) {
      return items;
    }

    const search = searchTerm.toLowerCase();
    return items.reduce<HierarchicalItem[]>((acc, item) => {
      const matchesSearch = item.label.toLowerCase().includes(search);
      const filteredChildren = item.children ? filterData(item.children, searchTerm) : undefined;

      if (matchesSearch || (filteredChildren && filteredChildren.length > 0)) {
        acc.push({
          ...item,
          children: filteredChildren
        });
      }

      return acc;
    }, []);
  };

  const baseData = areaDataByCountry;
  const filteredData = filterData(baseData, searchTerm);
  const countrySelectAllContentIds = useMemo(() => {
    if (searchTerm) return [];
    const ids: string[] = [];
    for (const region of areaDataByCountry) {
      ids.push(String(region.id));
      if (region.children && region.children.length > 0) {
        region.children.forEach((child) => ids.push(String(child.id)));
      }
    }
    return Array.from(new Set(ids));
  }, [searchTerm, selectedCountry, areaDataByCountry]);

  const countrySelectAllIds = useMemo(() => {
    if (searchTerm) return [];
    const countryId = COUNTRY_CONFIG[selectedCountry].id;
    const hasCountryId = areas.some((area) => area.countryId);
    const ids = hideCountryTabs || !hasCountryId
      ? countrySelectAllContentIds
      : [String(countryId), ...countrySelectAllContentIds.map(String)];
    return Array.from(new Set(ids.map(String)));
  }, [searchTerm, selectedCountry, countrySelectAllContentIds, hideCountryTabs, areas]);

  const isCountryAllSelected = useMemo(() => {
    if (countrySelectAllContentIds.length === 0) return false;
    const set = new Set(draftSelectedAreas.map(String));
    return countrySelectAllContentIds.every((id) => set.has(String(id)));
  }, [countrySelectAllContentIds, draftSelectedAreas]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" data-testid="modal-overlay">
      <div className="modal-container flex flex-col" data-testid="modal-container" data-modal="area">
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-amber-600 mt-1">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск регионов и городов"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349]"
            />
          </div>
        </div>

        {!hideCountryTabs && (
        <div className="px-6 pt-2 pb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCountry('RU')}
            className={`flex items-center px-3 py-[9px] rounded-[10px] border-2 transition-colors ${
              selectedCountry === 'RU'
                ? 'border-[#f97316] text-[#f97316] bg-[#F6F8FA]'
                : 'border-transparent text-[#111827] bg-[#F6F8FA]'
            }`}
          >
            <FlagRussia className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Россия</span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedCountry('KZ')}
            className={`flex items-center px-3 py-[9px] rounded-[10px] border-2 transition-colors ${
              selectedCountry === 'KZ'
                ? 'border-[#f97316] text-[#f97316] bg-[#F6F8FA]'
                : 'border-transparent text-[#111827] bg-[#F6F8FA]'
            }`}
          >
            <FlagKazakhstan className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Казахстан</span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedCountry('BY')}
            className={`flex items-center px-3 py-[9px] rounded-[10px] border-2 transition-colors ${
              selectedCountry === 'BY'
                ? 'border-[#f97316] text-[#f97316] bg-[#F6F8FA]'
                : 'border-transparent text-[#111827] bg-[#F6F8FA]'
            }`}
          >
            <FlagBelarus className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Беларусь</span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedCountry('AZ')}
            className={`flex items-center px-3 py-[9px] rounded-[10px] border-2 transition-colors ${
              selectedCountry === 'AZ'
         ? 'border-[#f97316] text-[#f97316] bg-[#F6F8FA]'
                : 'border-transparent text-[#111827] bg-[#F6F8FA]'
            }`}
          >
            <FlagAzerbaijan className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Азербайджан</span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedCountry('GE')}
            className={`flex items-center px-3 py-[9px] rounded-[10px] border-2 transition-colors ${
              selectedCountry === 'GE'
              ? 'border-[#f97316] text-[#f97316] bg-[#F6F8FA]'
                : 'border-transparent text-[#111827] bg-[#F6F8FA]'
            }`}
          >
            <FlagGeorgia className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Грузия</span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedCountry('KG')}
            className={`flex items-center px-3 py-[9px] rounded-[10px] border-2 transition-colors ${
              selectedCountry === 'KG'
               ? 'border-[#f97316] text-[#f97316] bg-[#F6F8FA]'
                : 'border-transparent text-[#111827] bg-[#F6F8FA]'
            }`}
          >
            <FlagKyrgyzstan className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Кыргызстан</span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedCountry('UZ')}
            className={`flex items-center px-3 py-[9px] rounded-[10px] border-2 transition-colors ${
              selectedCountry === 'UZ'
                ? 'border-[#f97316] text-[#f97316] bg-[#F6F8FA]'
                : 'border-transparent text-[#111827] bg-[#F6F8FA]'
            }`}
          >
            <FlagUzbekistan className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Узбекистан</span>
          </button>
        </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">Загрузка...</div>
            </div>
          )}
          
          {error && (
            <div className="flex justify-center items-center py-8">
              <div className="text-red-500">{error}</div>
            </div>
          )}
          
          {!loading && !error && filteredData.length === 0 && (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">Ничего не найдено</div>
            </div>
          )}
          
          {!singleSelect && !loading && !error && !searchTerm && countrySelectAllIds.length > 0 && (
            <div className="mb-3">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isCountryAllSelected}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setDraftSelectedAreas((prev) => {
                        const set = new Set(prev.map(String));
                        countrySelectAllIds.forEach((id) => set.add(String(id)));
                        return Array.from(set);
                      });
                    } else {
                      setDraftSelectedAreas((prev) => {
                        const remove = new Set(countrySelectAllIds.map(String));
                        return prev.filter((id) => !remove.has(String(id)));
                      });
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300 focus:ring-2"
                />
                <span className="text-sm text-gray-700 font-medium">Выбрать все</span>
              </label>
            </div>
          )}

          {!loading && !error && filteredData.length > 0 && (
            <div className="space-y-1">
              {filteredData.map((item) => (
                <HierarchicalCheckbox
                  key={item.id}
                  item={item}
                  checkedItems={draftSelectedAreas}
                  onToggle={handleToggle}
                  expandedItems={expandedItems}
                  onToggleExpanded={handleToggleExpanded}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={handleReset}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            Сбросить
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Отменить
            </button>
            <button
              onClick={handleSave}
              disabled={singleSelect ? draftSelectedAreas.length !== 1 : false}
              className={`px-4 py-2 rounded-lg transition-colors ${
                (singleSelect ? draftSelectedAreas.length !== 1 : false)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#e16349] text-white hover:bg-[#d14d3a]'
              }`}
            >
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AreaModal;
